import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cpu, Pencil, Plus, Trash2, Wifi } from 'lucide-react';
import {
  devicePresetSettingsSchema,
  devicePresetFieldIssues,
  devicePresetRefusals,
  primarySsidPort,
  ssidPortsForBand,
  type CreateDevicePresetInput,
  type DevicePreset,
  type DevicePresetSettings,
  type DevicePresetTarget,
  type Page,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/** What the tab reads; every dialog write refreshes it from the tasks tray. */
const PRESETS_KEY = ['device-presets'] as const;

/**
 * Device config presets — the device-SIDE bundles.
 *
 * The authorise presets are what the OLT is told about a subscriber; these are
 * what the ONU itself is told. A standard subscriber setup becomes one
 * selection on the ONU's own page instead of the same form filled the same way
 * every time.
 *
 * Nothing on this page reaches a chassis or a CPE. A preset is written to one
 * named ONU, from that ONU's page, after its exact commands have been shown.
 */

const TARGET_LABEL: Record<DevicePresetTarget, string> = {
  tr069: 'TR-069',
  omci: 'OMCI',
};

const EMPTY = devicePresetSettingsSchema.parse({});

/** What a preset actually sets, in the fewest words that stay true. */
function describe(preset: DevicePreset): string[] {
  const out: string[] = [];
  const { wifi, wan, lan } = preset.settings;
  const names = [wifi.ssid24, wifi.ssid5g].filter(Boolean);
  if (names.length > 0) out.push(`SSID ${names.join(' / ')}`);
  if (preset.hasWifiPassphrase) out.push('passphrase');
  if (wifi.radio24 !== null) out.push(`2.4G ${wifi.radio24 ? 'on' : 'off'}`);
  if (wifi.radio5g !== null) out.push(`5G ${wifi.radio5g ? 'on' : 'off'}`);
  if (wan.mode) out.push(`WAN ${wan.mode.toUpperCase()}${wan.vlanId ? ` VLAN ${wan.vlanId}` : ''}`);
  if (lan.ip) out.push(`LAN ${lan.ip}`);
  if (lan.dhcpEnabled !== null) out.push(`DHCP ${lan.dhcpEnabled ? 'on' : 'off'}`);
  return out;
}

export function DevicePresetsTab() {
  const { can } = useSession();
  const editable = can('olt.manage');

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<DevicePreset | null>(null);
  const [deleting, setDeleting] = useState<DevicePreset | null>(null);

  const query = useQuery({
    queryKey: PRESETS_KEY,
    queryFn: () => api.get<Page<DevicePreset>>('/device-presets'),
  });
  const items = query.data?.items ?? [];

  return (
    <>
      {/* No `PageHeader`: Presets owns the page title, this is a tab body. The
          description and the action still belong to the tab, because each kind
          of preset is a different thing and only one of them is on screen. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted">What the ONU itself is told — SSID, key, radios, WAN and LAN — saved under a name and applied to one subscriber at a time.</p>
        {editable ? (
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            New preset
          </Button>
        ) : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Presets" value={items.length} icon={<Cpu className="size-4" />} />
        <StatCard label="Over TR-069" value={items.filter((p) => p.target === 'tr069').length} />
        <StatCard label="Over OMCI" value={items.filter((p) => p.target === 'omci').length} />
        <StatCard
          label="Carrying a WiFi key"
          value={items.filter((p) => p.hasWifiPassphrase).length}
          icon={<Wifi className="size-4" />}
        />
      </div>

      <Card className="mt-5" title="Your presets">
        <p className="border-b border-line px-5 py-2 text-xs text-faint">
          Saving one here writes nothing to anything. A preset reaches an ONU from that ONU&rsquo;s
          own page, one subscriber at a time, after its exact commands have been shown.
        </p>

        {query.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : query.error ? (
          <div className="p-5">
            <ErrorNotice error={query.error} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Cpu className="size-8" />}
            title="No device presets yet"
            description="Create the standard subscriber setup once — the house SSID pattern, the WiFi key, DHCP on the WAN — and apply it from an ONU's page instead of filling the same form again."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Preset</th>
              <th className="th">Written over</th>
              <th className="th">Sets</th>
              <th className="th">Updated</th>
              <th className="th" />
            </THead>
            <TBody>
              {items.map((p) => (
                <TRow key={p.id}>
                  <td className="td">
                    <span className="text-xs font-medium text-ink">{p.name}</span>
                    {p.description && <p className="text-xs text-faint">{p.description}</p>}
                  </td>
                  <td className="td">
                    <Pill tone={p.target === 'tr069' ? 'primary' : 'info'}>
                      {TARGET_LABEL[p.target]}
                    </Pill>
                    {p.vendor && <Pill tone="neutral">{p.vendor.toUpperCase()}</Pill>}
                  </td>
                  <td className="td text-xs text-muted">
                    {describe(p).join(' · ') || <span className="text-faint">nothing yet</span>}
                  </td>
                  <td className="td text-xs text-muted">{formatRelative(p.updatedAt)}</td>
                  <td className="td">
                    {editable && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" icon={<Pencil className="size-3.5" />} onClick={() => setEditing(p)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => setDeleting(p)}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {creating && <PresetDialog onClose={() => setCreating(false)} />}
      {editing && <PresetDialog preset={editing} onClose={() => setEditing(null)} />}
      {deleting && <DeleteDialog preset={deleting} onClose={() => setDeleting(null)} />}
    </>
  );
}

/** A tri-state control: leave it alone, on, or off. Absent means untouched. */
function TriState({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <select
        className="input"
        value={value === null ? '' : value ? 'on' : 'off'}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'on')}
      >
        <option value="">Leave as it is</option>
        <option value="on">On</option>
        <option value="off">Off</option>
      </select>
    </Field>
  );
}

/**
 * Create or edit a preset.
 *
 * `target` is chosen once and then fixed: it decides which write path every
 * field below means, so changing it would silently reinterpret the whole
 * bundle. Fields the chosen target cannot write are not rendered at all — an
 * input that quietly does nothing is worse than one that is not offered — and
 * the API refuses the same combinations, so the two cannot drift.
 *
 * Saving closes the dialog at once and the write runs in the tasks tray; the
 * list refreshes when it lands. The judgements above are made BEFORE that, on
 * the button — an invalid bundle never leaves the dialog.
 */
function PresetDialog({
  preset,
  onClose,
}: {
  preset?: DevicePreset;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const editingExisting = preset !== undefined;
  const [name, setName] = useState(preset?.name ?? '');
  const [description, setDescription] = useState(preset?.description ?? '');
  const [target, setTarget] = useState<DevicePresetTarget>(preset?.target ?? 'tr069');
  const [settings, setSettings] = useState<DevicePresetSettings>(preset?.settings ?? EMPTY);
  const [wifiPassphrase, setWifiPassphrase] = useState('');
  const [pppoePassword, setPppoePassword] = useState('');

  const omci = target === 'omci';
  const set = <K extends keyof DevicePresetSettings>(
    group: K,
    patch: Partial<DevicePresetSettings[K]>,
  ) => setSettings((s) => ({ ...s, [group]: { ...s[group], ...patch } }));

  // The same two judgements the API makes, in the same order, so a preset is
  // never accepted here and refused there.
  const issues = [...devicePresetRefusals(settings), ...devicePresetFieldIssues(target, settings)];
  const ready = name.trim().length > 0 && issues.length === 0;

  const save = () => {
    // Snapshotted: the dialog is gone by the time the write settles.
    const body: Partial<CreateDevicePresetInput> = {
      name: name.trim(),
      description: description.trim() || null,
      settings,
      // Blank leaves the stored key alone rather than clearing it — a
      // password field that empties a secret on every unrelated edit is how
      // a fleet loses its WiFi key.
      ...(wifiPassphrase ? { wifiPassphrase } : {}),
      ...(pppoePassword ? { pppoePassword } : {}),
    };
    const shown = name.trim();
    onClose();
    tasks.start({
      label: `${editingExisting ? 'Save' : 'Create'} device preset ${shown}`,
      run: () =>
        preset
          ? api.patch<DevicePreset>(`/device-presets/${encodeURIComponent(preset.id)}`, body)
          : api.post<DevicePreset>('/device-presets', { ...body, target }),
      done: () => (editingExisting ? `“${shown}” updated.` : `“${shown}” created.`),
      invalidate: [PRESETS_KEY],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {editingExisting ? `Edit ${preset.name}` : 'New device preset'}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            What the ONU is told. Anything left as it is stays untouched on the subscriber&rsquo;s
            unit.
          </p>
        </header>

        <div className="max-h-[65vh] space-y-5 overflow-y-auto p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Standard home" />
            </Field>
            <Field
              label="Written over"
              hint={
                editingExisting
                  ? 'Fixed — it decides what every field below means'
                  : omci
                    ? 'Through the OLT, no ACS needed. ZTE chassis only.'
                    : 'Through the ACS, against the paths the CPE reports.'
              }
            >
              <select
                className="input"
                value={target}
                disabled={editingExisting}
                onChange={(e) => setTarget(e.target.value as DevicePresetTarget)}
              >
                <option value="tr069">TR-069 (through the ACS)</option>
                <option value="omci">OMCI (through the OLT)</option>
              </select>
            </Field>
          </div>

          <Field label="Description" hint="Optional — what this setup is, for whoever comes next">
            <Input
              value={description}
              maxLength={200}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Residential router: house SSID, DHCP WAN on VLAN 5"
            />
          </Field>

          <section className="rounded-lg border border-line p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">WiFi</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="2.4 GHz SSID" hint="The primary 2.4 GHz network">
                <Input
                  value={settings.wifi.ssid24 ?? ''}
                  maxLength={32}
                  placeholder="Leave as it is"
                  onChange={(e) => set('wifi', { ssid24: e.target.value || null })}
                />
              </Field>
              <Field label="5 GHz SSID" hint="Left alone when blank — no automatic mirroring">
                <Input
                  value={settings.wifi.ssid5g ?? ''}
                  maxLength={32}
                  placeholder="Leave as it is"
                  onChange={(e) => set('wifi', { ssid5g: e.target.value || null })}
                />
              </Field>
              {/* Over OMCI a band has to be turned into an interface, because
                  the chassis never says which radio one is on. The default is
                  the band's primary; the picker offers only that band's own
                  interfaces, so a name cannot land on the wrong radio. */}
              {omci && (
                <>
                  <Field label="2.4 GHz interface" hint="Where the 2.4 GHz name is written">
                    <select
                      className="input"
                      value={settings.wifi.ssidPort24 ?? ''}
                      onChange={(e) => set('wifi', { ssidPort24: e.target.value || null })}
                    >
                      <option value="">{primarySsidPort('2.4G')} (primary)</option>
                      {ssidPortsForBand('2.4G')
                        .slice(1)
                        .map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                    </select>
                  </Field>
                  <Field label="5 GHz interface" hint="Where the 5 GHz name is written">
                    <select
                      className="input"
                      value={settings.wifi.ssidPort5g ?? ''}
                      onChange={(e) => set('wifi', { ssidPort5g: e.target.value || null })}
                    >
                      <option value="">{primarySsidPort('5G')} (primary)</option>
                      {ssidPortsForBand('5G')
                        .slice(1)
                        .map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                    </select>
                  </Field>
                </>
              )}
              <Field
                label="Passphrase"
                hint={
                  preset?.hasWifiPassphrase
                    ? 'A key is held. Blank keeps it; typing one replaces it.'
                    : '8–63 characters. Stored encrypted and never shown again.'
                }
              >
                <Input
                  type="password"
                  value={wifiPassphrase}
                  maxLength={63}
                  placeholder={preset?.hasWifiPassphrase ? '••••••••' : '8–63 characters'}
                  onChange={(e) => setWifiPassphrase(e.target.value)}
                />
              </Field>
              {omci && (
                <Field label="WPA mode" hint="Rides with the key; the chassis defaults to mixed">
                  <select
                    className="input"
                    value={settings.wifi.wpaMode ?? ''}
                    onChange={(e) =>
                      set('wifi', { wpaMode: (e.target.value || null) as DevicePresetSettings['wifi']['wpaMode'] })
                    }
                  >
                    <option value="">Leave as it is</option>
                    <option value="wpa-wpa2-psk">WPA/WPA2 mixed</option>
                    <option value="wpa2-psk">WPA2</option>
                    <option value="wpa-psk">WPA</option>
                  </select>
                </Field>
              )}
              <TriState
                label="2.4 GHz radio"
                hint="Off drops every client on it"
                value={settings.wifi.radio24}
                onChange={(radio24) => set('wifi', { radio24 })}
              />
              <TriState
                label="5 GHz radio"
                hint="Not every model has one"
                value={settings.wifi.radio5g}
                onChange={(radio5g) => set('wifi', { radio5g })}
              />
              {omci && (
                <>
                  <Field label="2.4 GHz channel" hint="1–13, and only with the radio turned on">
                    <Input
                      value={settings.wifi.channel24 ?? ''}
                      inputMode="numeric"
                      placeholder="Leave as it is"
                      onChange={(e) =>
                        set('wifi', { channel24: e.target.value ? Number(e.target.value) : null })
                      }
                    />
                  </Field>
                  <TriState
                    label="Broadcast the name"
                    hint="Applies to each band this preset names"
                    value={settings.wifi.hidden === null ? null : !settings.wifi.hidden}
                    onChange={(v) => set('wifi', { hidden: v === null ? null : !v })}
                  />
                </>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-line p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">WAN</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Static is not offered on either path, and the hint says why
                  rather than leaving its absence to read as a bug. */}
              <Field
                label="How the WAN gets an address"
                hint="A preset is applied more than once; a static address belongs to one subscriber. Set one on that ONU's own WAN."
              >
                <select
                  className="input"
                  value={settings.wan.mode ?? ''}
                  onChange={(e) =>
                    set('wan', { mode: (e.target.value || null) as DevicePresetSettings['wan']['mode'] })
                  }
                >
                  <option value="">Leave as it is</option>
                  <option value="dhcp">DHCP</option>
                  <option value="pppoe">PPPoE</option>
                </select>
              </Field>
              {/* The one VLAN field in this platform that is legitimately
                  free-text: a preset belongs to the org and names no chassis,
                  so there is no VLAN table to offer from. It is checked when
                  the preset is APPLIED, against the OLT the ONU is actually on
                  — which is also where a management VLAN is refused. */}
              <Field
                label="WAN VLAN"
                hint="A subscriber VLAN. Checked against the chassis when this preset is applied, not here — a preset names no OLT."
              >
                <Input
                  value={settings.wan.vlanId ?? ''}
                  inputMode="numeric"
                  placeholder="Leave as it is"
                  onChange={(e) => set('wan', { vlanId: e.target.value ? Number(e.target.value) : null })}
                />
              </Field>
              {settings.wan.mode === 'pppoe' && (
                <>
                  <Field
                    label="PPPoE account"
                    hint="One account on many ONUs is one subscriber's session"
                  >
                    <Input
                      value={settings.wan.pppoeUsername ?? ''}
                      maxLength={120}
                      onChange={(e) => set('wan', { pppoeUsername: e.target.value || null })}
                    />
                  </Field>
                  <Field
                    label="PPPoE password"
                    hint={preset?.hasPppoePassword ? 'Held. Blank keeps it.' : 'Stored encrypted.'}
                  >
                    <Input
                      type="password"
                      value={pppoePassword}
                      maxLength={128}
                      placeholder={preset?.hasPppoePassword ? '••••••••' : ''}
                      onChange={(e) => setPppoePassword(e.target.value)}
                    />
                  </Field>
                </>
              )}
              {!omci && (
                <Field
                  label="WAN DNS servers"
                  hint="Comma-separated, as the CPE takes them. Written on its own, so it works on DHCP and PPPoE alike."
                >
                  <Input
                    value={settings.wan.dns ?? ''}
                    maxLength={120}
                    placeholder="Leave as it is"
                    onChange={(e) => set('wan', { dns: e.target.value || null })}
                  />
                </Field>
              )}
            </div>
          </section>

          {!omci && (
            <section className="rounded-lg border border-line p-4">
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">LAN</h3>
              <p className="mb-3 text-xs text-faint">
                Written over TR-069 only — the chassis can read an ONU&rsquo;s DHCP pool but has no
                verified command to write one.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="LAN address">
                  <Input
                    value={settings.lan.ip ?? ''}
                    placeholder="192.168.1.1"
                    onChange={(e) => set('lan', { ip: e.target.value || null })}
                  />
                </Field>
                <Field label="LAN subnet mask">
                  <Input
                    value={settings.lan.subnetMask ?? ''}
                    placeholder="255.255.255.0"
                    onChange={(e) => set('lan', { subnetMask: e.target.value || null })}
                  />
                </Field>
                <TriState
                  label="DHCP server"
                  value={settings.lan.dhcpEnabled}
                  onChange={(dhcpEnabled) => set('lan', { dhcpEnabled })}
                />
                <Field label="DHCP lease time" hint="Seconds">
                  <Input
                    value={settings.lan.dhcpLeaseTime ?? ''}
                    inputMode="numeric"
                    placeholder="Leave as it is"
                    onChange={(e) =>
                      set('lan', { dhcpLeaseTime: e.target.value ? Number(e.target.value) : null })
                    }
                  />
                </Field>
                <Field label="DHCP pool start">
                  <Input
                    value={settings.lan.dhcpStart ?? ''}
                    placeholder="192.168.1.100"
                    onChange={(e) => set('lan', { dhcpStart: e.target.value || null })}
                  />
                </Field>
                <Field label="DHCP pool end">
                  <Input
                    value={settings.lan.dhcpEnd ?? ''}
                    placeholder="192.168.1.200"
                    onChange={(e) => set('lan', { dhcpEnd: e.target.value || null })}
                  />
                </Field>
                <Field label="LAN DNS servers" hint="What the pool hands out">
                  <Input
                    value={settings.lan.dns ?? ''}
                    maxLength={120}
                    onChange={(e) => set('lan', { dns: e.target.value || null })}
                  />
                </Field>
              </div>
            </section>
          )}

          {issues.map((issue) => (
            <p key={issue} className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
              {issue}
            </p>
          ))}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={save}>
            {editingExisting ? 'Save' : 'Create'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Removes the preset. Nothing already written to an ONU is undone — a preset
 * is a habit, and deleting it changes what is offered next time rather than
 * what any subscriber is running.
 */
function DeleteDialog({ preset, onClose }: { preset: DevicePreset; onClose: () => void }) {
  const tasks = useTasks();
  const call = () => {
    onClose();
    tasks.start({
      label: `Delete device preset ${preset.name}`,
      run: () => api.delete(`/device-presets/${encodeURIComponent(preset.id)}`),
      done: () => `“${preset.name}” deleted.`,
      invalidate: [PRESETS_KEY],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Delete {preset.name}</h2>
          <p className="mt-0.5 text-xs text-muted">
            Removes the preset so it can no longer be applied. Every ONU it was already written to
            keeps what it was given.
          </p>
        </header>
        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={call}>
            Delete
          </Button>
        </footer>
      </div>
    </div>
  );
}
